import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  styled,
} from "@mui/material";
import { getProposalsByLenderId, getProposalByLenderAndId } from "../../services/proposalService";
import ProposalList from '../../components/common/ProposalList/ProposalList';
import ProposalHeader from '../../components/common/ProposalList/ProposalHeader';
import { toast } from "react-toastify";

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "60vh",
});

const formatProposal = (proposalData) => ({
  id: proposalData.id,
  uuid: proposalData.uuid,
  proposalId: `PROP-${proposalData.proposal.id}`,
  status: proposalData.status || proposalData.proposal.status || "new",
  createDatetime: proposalData.createDatetime,
  createOpName: proposalData.createOpName,
  borrowerId: proposalData.proposal.borrowerId,
  userId: proposalData.proposal.userId,
  lenderId: proposalData.lender?.lenderId || "N/A",
  applicationId: proposalData.proposal?.applicationForm?.applicationId || "N/A",
  cma: proposalData.proposal.cma,
  onePager: proposalData.proposal.onePager,
  submittedDate: new Date(proposalData.createDatetime).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }),
  proposal: proposalData.proposal,
  applicationForm: proposalData.proposal.applicationForm,
});

const LenderProposals = () => {
  const navigate = useNavigate();
  const { uuid: lenderUuid } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const status = activeTab === 1 ? "rejected" : "all";
      const response = await getProposalsByLenderId(lenderUuid, status);

      if (response?.statusCode === 200) {
        const proposalsArray = response?.result?.result || [];
        const formattedProposals = proposalsArray.map(formatProposal);
        setProposals(formattedProposals);
      } else {
        setProposals([]);
      }
    } catch (error) {
      toast.error(error.message);
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, lenderUuid]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const handleViewDetails = useCallback(async (proposal) => {
    try {
      const lenderId = proposal.lenderId;
      const proposalId = proposal.proposal?.id;

      if (!lenderId || !proposalId) {
        toast.error('Missing lenderId or proposalId');
        return;
      }

      const response = await getProposalByLenderAndId(lenderId, proposalId);
      if (response?.statusCode === 200) {
        navigate(`/proposals/details/${proposal.uuid}`, {
          state: {
            proposalData: proposal,
            applicationFormData: proposal.applicationForm,
            otherOffers: response?.result?.otherOffers
          },
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, [navigate]);

  const filteredProposals = useMemo(() => {
    if (!Array.isArray(proposals)) return [];

    const searchTerm = searchQuery.toLowerCase();
    return proposals.filter(proposal =>
      proposal.proposalId?.toLowerCase().includes(searchTerm) ||
      proposal.createOpName?.toLowerCase().includes(searchTerm) ||
      proposal.status?.toLowerCase().includes(searchTerm)
    );
  }, [proposals, searchQuery]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <Container maxWidth="xl">
      <ProposalHeader
        title="Lender Proposals"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <ProposalList
        proposals={filteredProposals}
        onViewDetails={handleViewDetails}
        groupedByMonth={true}
        showLenderId={true}
      />
    </Container>
  );
};

export default React.memo(LenderProposals);
